import React, { useRef, useState } from 'react';
import { Box, Typography, IconButton, Tooltip, LinearProgress, Chip } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ShowAddDocCollaborateur from './ShowAddDocCollaborateur';

const MAX_SIZE = 50 * 1024 * 1024;
const ACCEPT = '.pdf,image/png,image/jpeg,image/jpg';

const STATUS = {
  saved: { label: 'Enregistré', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', icon: <CheckCircleIcon /> },
  pending: { label: 'Prêt à enregistrer', color: '#b45309', bg: '#fffbeb', border: '#fcd34d', icon: <ScheduleIcon /> },
  missing: { label: 'Manquant', color: '#64748b', bg: '#fff', border: '#e2e8f0', icon: <ReportProblemOutlinedIcon /> },
};

const formatSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} Mo` : `${Math.max(1, Math.round(bytes / 1024))} Ko`;

const normalize = (s) => String(s || '').trim().toLowerCase();

const FileLine = ({ name, meta, isImage, onView, onRemove, onDelete }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1,
      py: 0.75,
      borderRadius: '8px',
      bgcolor: '#fff',
      border: '1px solid #e2e8f0',
      minWidth: 0,
    }}
  >
    {isImage
      ? <ImageOutlinedIcon sx={{ fontSize: 20, color: '#0556a4' }} />
      : <PictureAsPdfOutlinedIcon sx={{ fontSize: 20, color: '#dc2626' }} />}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography noWrap title={name} sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#0f172a' }}>{name}</Typography>
      {meta && <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>{meta}</Typography>}
    </Box>
    {onView && (
      <Tooltip title="Voir le document">
        <IconButton size="small" onClick={onView} sx={{ color: '#03407b' }}>
          <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
    )}
    {onDelete && (
      <Tooltip title="Supprimer le document">
        <IconButton size="small" onClick={onDelete} sx={{ color: '#94a3b8', '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' } }}>
          <DeleteOutlinedIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
    )}
    {onRemove && (
      <Tooltip title="Retirer">
        <IconButton size="small" onClick={onRemove} sx={{ color: '#94a3b8', '&:hover': { color: '#dc2626' } }}>
          <CloseIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

const DocTile = ({ docType, pendingFile, savedDoc, onFile, onView, onDelete, disabled, onError }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const status = pendingFile ? 'pending' : savedDoc ? 'saved' : 'missing';
  const s = STATUS[status];

  const pick = (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    const okType = file.type === 'application/pdf' || file.type.startsWith('image/');
    if (!okType) return onError('Seuls les fichiers PDF et images (JPG, PNG) sont autorisés.');
    if (file.size > MAX_SIZE) return onError('Fichier trop volumineux (50 Mo max).');
    onFile(file);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        p: 1.5,
        borderRadius: '12px',
        border: `1px solid ${s.border}`,
        bgcolor: s.bg,
        transition: 'border-color .15s, background-color .15s',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '8px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: status === 'missing' ? '#f1f5f9' : '#fff',
            color: s.color,
            '& svg': { fontSize: 17 },
          }}
        >
          {s.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap title={docType} sx={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a' }}>
            {docType}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: s.color }}>{s.label}</Typography>
        </Box>
      </Box>

      {pendingFile && (
        <FileLine
          name={pendingFile.name}
          meta={`${formatSize(pendingFile.size)} · sera envoyé à l'enregistrement`}
          isImage={pendingFile.type?.startsWith('image/')}
          onRemove={() => onFile(null)}
        />
      )}
      {!pendingFile && savedDoc && (
        <FileLine
          name={savedDoc.fileName || docType}
          meta="Déjà enregistré"
          isImage={/\.(png|jpe?g)$/i.test(savedDoc.fileName || '')}
          onView={() => onView(savedDoc)}
          onDelete={onDelete && !disabled ? () => onDelete(savedDoc) : undefined}
        />
      )}

      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (!disabled) pick(e.dataTransfer.files);
        }}
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: status === 'missing' ? 'center' : 'flex-start',
          gap: 1.25,
          px: 1.5,
          py: status === 'missing' ? 2.5 : 1,
          borderRadius: '10px',
          border: '1.5px dashed',
          borderColor: dragOver ? '#03407b' : '#bcd6ee',
          bgcolor: dragOver ? '#ebf4fb' : 'rgba(255,255,255,.7)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'all .15s',
          '&:hover': disabled ? {} : { borderColor: '#03407b', bgcolor: '#f0f7fd' },
          outline: 'none',
          '&:focus-visible': { boxShadow: '0 0 0 2px #0556a4' },
        }}
      >
        <CloudUploadOutlinedIcon sx={{ color: '#03407b', fontSize: status === 'missing' ? 26 : 20 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#03407b' }}>
            {dragOver ? 'Déposez le fichier ici' : status === 'missing' ? 'Glissez un fichier ou cliquez' : 'Remplacer le fichier'}
          </Typography>
          {status === 'missing' && (
            <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>PDF, JPG ou PNG · 50 Mo max</Typography>
          )}
        </Box>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          hidden
          onChange={(e) => {
            pick(e.target.files);
            e.target.value = '';
          }}
        />
      </Box>
    </Box>
  );
};

const RequiredDocsPanel = ({ requiredDocs, uploadedFiles, onFileChange, onDeleteDoc, savedDocs = [], contratNom, disabled, snackbarShowMessage, children }) => {
  const [viewDoc, setViewDoc] = useState(null);

  const findSaved = (docType) => {
    const matches = savedDocs.filter((d) => normalize(d.category) === normalize(docType));
    return matches[matches.length - 1] || null;
  };

  const savedCount = requiredDocs.filter((d) => findSaved(d)).length;
  const pendingCount = requiredDocs.filter((d) => uploadedFiles[d] && !findSaved(d)).length;
  const doneCount = savedCount + pendingCount;
  const total = requiredDocs.length;
  const percent = total ? Math.round((doneCount / total) * 100) : 0;
  const missing = total - doneCount;
  const ringColor = percent === 100 ? '#16a34a' : '#03407b';
  const notify = (msg) => snackbarShowMessage?.(msg, 'error', 4000);

  const hasChecklist = !!contratNom && total > 0;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '300px minmax(0, 1fr)' },
        gap: 2,
        alignItems: 'start',
      }}
    >
      <Box
        sx={{
          p: 2,
          borderRadius: '12px',
          bgcolor: '#f8fbfe',
          border: '1px solid #e2edf7',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {hasChecklist && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ position: 'relative', width: 58, height: 58, flexShrink: 0 }}>
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: `conic-gradient(${ringColor} ${percent * 3.6}deg, #e2e8f0 0deg)`,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 6,
                    borderRadius: '50%',
                    bgcolor: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                  }}
                >
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
                    {doneCount}/{total}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.3 }}>
                  {missing === 0
                    ? pendingCount > 0
                      ? 'Dossier complet : pensez à sauvegarder'
                      : 'Dossier complet'
                    : `${missing} document${missing > 1 ? 's' : ''} manquant${missing > 1 ? 's' : ''}`}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
                  Contrat {contratNom.toUpperCase()} · {percent}% fourni
                </Typography>
              </Box>
            </Box>

            <LinearProgress
              variant="determinate"
              value={percent}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: '#e2e8f0',
                '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: ringColor },
              }}
            />

            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              <Chip size="small" label={`${savedCount} enregistré${savedCount > 1 ? 's' : ''}`} sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#f0fdf4', color: '#16a34a' }} />
              {pendingCount > 0 && (
                <Chip size="small" label={`${pendingCount} à enregistrer`} sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#fffbeb', color: '#b45309' }} />
              )}
              {missing > 0 && (
                <Chip size="small" label={`${missing} manquant${missing > 1 ? 's' : ''}`} sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#f1f5f9', color: '#475569' }} />
              )}
            </Box>

            <Box sx={{ borderTop: '1px solid #e2edf7' }} />
          </>
        )}

        {children}
      </Box>

      {hasChecklist ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1.5 }}>
          {requiredDocs.map((docType) => (
            <DocTile
              key={docType}
              docType={docType}
              pendingFile={uploadedFiles[docType]}
              savedDoc={findSaved(docType)}
              onFile={(file) => onFileChange(docType, file ? [file] : [])}
              onView={setViewDoc}
              onDelete={onDeleteDoc}
              disabled={disabled}
              onError={notify}
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2.5,
            minHeight: 120,
            borderRadius: '12px',
            border: '1px dashed #cbd5e1',
            bgcolor: '#f8fafc',
          }}
        >
          <CloudUploadOutlinedIcon sx={{ color: '#94a3b8', fontSize: 30 }} />
          <Typography sx={{ color: '#64748b', fontSize: '0.84rem' }}>
            {contratNom
              ? 'Aucun document requis pour ce type de contrat. Vous pouvez les définir dans Outils › Gestion documents.'
              : 'Choisissez un contrat pour afficher la liste des documents à fournir.'}
          </Typography>
        </Box>
      )}

      {viewDoc && (
        <ShowAddDocCollaborateur
          open
          onClose={() => setViewDoc(null)}
          files={[viewDoc]}
          categorie={viewDoc.category}
          fileName={viewDoc.fileName}
        />
      )}
    </Box>
  );
};

export default RequiredDocsPanel;
